import type { BillingAction, BillingOverview, BillingPlan, BillingStatus, CommercialStatus } from '../types/billing';
import type { UserRole } from '../types/auth';
import type { Tone } from '../components/ui/primitives';
import { DEFAULT_STORE_TIMEZONE } from './timezones';

// ONB-007 (Macrofase B) — cómo se DICE la facturación. Puro (sin red, sin React): la pantalla «Plan y
// facturación» y el aviso global lo usan para traducir lo que ya decidió el servidor. Aquí no se calcula
// ningún acceso ni se compara ninguna fecha con el reloj del navegador.
//
// Tono humano: la información del negocio siempre sigue ahí; nunca se habla de bloqueo ni de castigo.

export const BILLING_TITLE = 'Plan y facturación';

const STATUS_LABEL: Record<CommercialStatus, string> = {
  MANAGED: 'Administrado por Negocio360',
  TRIALING: 'Prueba gratis',
  ACTIVE: 'Activo',
  PAST_DUE: 'Necesita atención',
  READ_ONLY: 'Solo lectura',
};

const STATUS_TONE: Record<CommercialStatus, 'neutral' | Tone> = {
  MANAGED: 'neutral',
  TRIALING: 'info',
  ACTIVE: 'success',
  PAST_DUE: 'warning',
  READ_ONLY: 'warning',
};

export function statusLabel(status: CommercialStatus): string {
  return STATUS_LABEL[status];
}

export function statusTone(status: CommercialStatus): 'neutral' | Tone {
  return STATUS_TONE[status];
}

/** Fecha larga en español, en la zona del negocio (la de su tienda). Solo para mostrar. */
export function formatBillingDate(iso: string | null | undefined, timeZone: string = DEFAULT_STORE_TIMEZONE): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  try {
    return new Intl.DateTimeFormat('es-MX', { dateStyle: 'long', timeZone }).format(date);
  } catch {
    return new Intl.DateTimeFormat('es-MX', { dateStyle: 'long', timeZone: DEFAULT_STORE_TIMEZONE }).format(date);
  }
}

/**
 * La primera de dos fechas que dio el servidor (null si ninguna). Con el pago atrasado, lo normal dura hasta la gracia
 * o hasta la cancelación programada, lo que pase antes (revisión 6b, R1). No usa el reloj del navegador.
 */
function earliest(a: string | null | undefined, b: string | null | undefined): string | null {
  const times = [a, b].filter((iso): iso is string => Boolean(iso) && !Number.isNaN(Date.parse(iso as string)));
  if (times.length === 0) return null;
  return times.reduce((first, iso) => (Date.parse(iso) < Date.parse(first) ? iso : first));
}

/** Las líneas que explican el estado (qué pasa y desde/hasta cuándo). */
export function statusSummary(view: BillingOverview, timeZone?: string): string[] {
  const date = (iso: string | null | undefined) => formatBillingDate(iso, timeZone);
  const planName = view.subscription?.plan?.name ?? view.plan.name;
  switch (view.status) {
    case 'MANAGED':
      return ['El plan de este negocio lo administra Negocio360. No necesitas hacer nada aquí.'];
    case 'TRIALING': {
      if (view.subscription) {
        const first = date(view.subscription.firstChargeAt ?? view.trialEndsAt);
        return [
          `Ya elegiste el plan ${planName} y guardaste tu forma de pago.`,
          first ? `Tu prueba gratis sigue hasta el ${first}; no se cobra nada antes de esa fecha.` : 'No se cobra nada antes de que termine tu prueba gratis.',
          ...(view.cancelEffectiveAt ? [`Programaste la cancelación: tu acceso sigue hasta el ${date(view.cancelEffectiveAt)}.`] : []),
        ];
      }
      const end = date(view.trialEndsAt);
      return [
        end ? `Tu prueba gratis termina el ${end}.` : 'Estás en tu prueba gratis.',
        'Puedes elegir un plan cuando quieras: conservas todos los días que te quedan y no se cobra nada antes de que termine.',
      ];
    }
    case 'ACTIVE':
      if (view.cancelEffectiveAt) return [`Tu plan seguirá activo hasta el ${date(view.cancelEffectiveAt)}.`, 'Después, tu información quedará disponible para consultarla y podrás volver cuando quieras.'];
      return [
        `Tu plan ${planName} está activo.`,
        ...(view.currentPeriodEnd ? [`Siguiente renovación: ${date(view.currentPeriodEnd)}.`] : []),
      ];
    case 'PAST_DUE': {
      const until = view.graceEndsAt ? earliest(view.graceEndsAt, view.cancelEffectiveAt) : null;
      return [
        'Necesitamos actualizar tu forma de pago.',
        until ? `Puedes seguir trabajando con normalidad hasta el ${date(until)}.` : 'Por ahora puedes seguir trabajando con normalidad.',
        ...(view.cancelEffectiveAt ? [`Programaste la cancelación: tu plan no se renovará después del ${date(view.cancelEffectiveAt)}.`] : []),
      ];
    }
    case 'READ_ONLY':
    default:
      return [
        'Tu información está aquí. Para seguir registrando movimientos, activa un plan.',
        readOnlyReasonText(view, timeZone),
        ...(view.readOnlyReason === 'PAYMENT_OVERDUE' && view.cancelEffectiveAt ? [`Programaste la cancelación: tu plan no se renovará después del ${date(view.cancelEffectiveAt)}.`] : []),
      ];
  }
}

const AFTER_CANCEL = 'Después podrás consultar toda tu información y volver a activar tu plan cuando quieras. No se borra nada.';

/** ¿La gracia termina ANTES que el periodo? Solo entonces pagar alarga lo normal (revisión 6b, R1). */
function graceBefore(graceIso: string | null | undefined, endIso: string | null | undefined): boolean {
  return Boolean(graceIso && endIso && Date.parse(graceIso) < Date.parse(endIso));
}

/**
 * Lo que se confirma antes de cancelar al final del periodo. Revisión 6 (LOW-1): con el pago atrasado el cobro del
 * periodo sigue pendiente y lo normal dura hasta la gracia (salvo que pague), o hasta el fin del periodo si la gracia
 * termina después (6b, R1); solo `past_due` se reintenta, `unpaid` ya no (6b, R3). En solo lectura no hay nada
 * «activo» que prometer.
 */
export function cancelConfirmText(view: BillingOverview, timeZone?: string): string {
  const date = (iso: string | null | undefined) => formatBillingDate(iso, timeZone);
  const periodEndIso = view.subscription?.currentPeriodEnd ?? view.currentPeriodEnd;
  const periodEnd = date(periodEndIso);
  if (view.status === 'PAST_DUE') {
    const pending = view.subscription?.status === 'PAST_DUE'
      ? 'Tu plan ya no se renovará. El pago de tu periodo actual sigue pendiente y se seguirá intentando cobrar con tu forma de pago registrada.'
      : 'Tu plan ya no se renovará. El pago de tu periodo actual sigue pendiente.';
    const grace = date(view.graceEndsAt);
    const until = !grace ? 'Por ahora puedes seguir trabajando con normalidad.'
      : graceBefore(view.graceEndsAt, periodEndIso) ? `Puedes seguir trabajando con normalidad hasta el ${grace}, y hasta el ${periodEnd} si el pago pendiente se cobra (actualiza tu forma de pago).`
        : `Puedes seguir trabajando con normalidad hasta el ${periodEnd ?? grace}.`;
    return [pending, until, AFTER_CANCEL].join(' ');
  }
  if (view.status === 'READ_ONLY') {
    return [
      'Tu plan ya no se renovará. Hoy tu negocio está en modo de consulta porque el pago de tu periodo actual sigue pendiente.',
      periodEnd ? `Si actualizas tu forma de pago y se cobra, podrás registrar movimientos hasta el ${periodEnd}.` : null,
      'Tu información sigue aquí y puedes volver a activar tu plan cuando quieras. No se borra nada.',
    ].filter(Boolean).join(' ');
  }
  return `${periodEnd ? `Tu plan seguirá activo hasta el ${periodEnd}.` : 'Tu plan seguirá activo hasta el final del periodo.'} ${AFTER_CANCEL}`;
}

/** El mensaje después de cancelar, con el estado que devolvió el servidor (revisión 6, LOW-1). */
export function cancelDoneText(view: BillingOverview, timeZone?: string): string {
  if (view.status === 'PAST_DUE') {
    const grace = formatBillingDate(view.graceEndsAt, timeZone);
    const end = formatBillingDate(view.cancelEffectiveAt, timeZone);
    // Actualizar la tarjeta no cobra por sí solo (una `unpaid` ya no se reintenta): lo que cuenta es que se cobre
    // (revisión 6c, INFO-c).
    if (grace && end) {
      return graceBefore(view.graceEndsAt, view.cancelEffectiveAt)
        ? `Listo. Tu plan ya no se renovará. Si el pago pendiente se cobra antes del ${grace} (actualiza tu forma de pago), seguirás trabajando con normalidad hasta el ${end}.`
        : `Listo. Tu plan ya no se renovará. Puedes seguir trabajando con normalidad hasta el ${end}.`;
    }
    return grace
      ? `Listo. Tu plan ya no se renovará. Si el pago pendiente se cobra antes del ${grace} (actualiza tu forma de pago), seguirás trabajando con normalidad.`
      : 'Listo. Tu plan ya no se renovará.';
  }
  if (view.status === 'READ_ONLY') return 'Listo. Tu plan ya no se renovará.';
  const end = formatBillingDate(view.cancelEffectiveAt, timeZone);
  return end ? `Listo. Tu plan seguirá activo hasta el ${end}.` : 'Listo. Tu plan seguirá activo hasta el final del periodo.';
}

function readOnlyReasonText(view: BillingStatus, timeZone?: string): string {
  switch (view.readOnlyReason) {
    case 'TRIAL_EXPIRED': {
      const end = formatBillingDate(view.trialEndsAt, timeZone);
      return end ? `Tu prueba gratis terminó el ${end}.` : 'Tu prueba gratis terminó.';
    }
    case 'PAYMENT_OVERDUE':
      return 'No pudimos cobrar tu plan a tiempo.';
    case 'PAYMENT_INCOMPLETE':
      return 'El primer pago de tu plan no se completó.';
    case 'SUBSCRIPTION_ENDED':
    default:
      return 'Tu plan terminó.';
  }
}

/** Texto del botón de cada acción que el servidor permite. */
export function actionLabel(action: BillingAction, status: CommercialStatus | undefined): string {
  switch (action) {
    case 'CHOOSE_PLAN':
      return status === 'READ_ONLY' ? 'Activar plan' : 'Elegir plan';
    case 'UPDATE_PAYMENT_METHOD':
      return 'Actualizar pago';
    case 'MANAGE_SUBSCRIPTION':
      // El portal del proveedor solo muestra facturas y forma de pago: cancelar y renovar están aquí, en Negocio360.
      return 'Ver pagos y facturas';
    case 'CANCEL_AT_PERIOD_END':
      return 'Cancelar al final del periodo';
    case 'RESUME_SUBSCRIPTION':
    default:
      return 'Mantener mi plan';
  }
}

export interface CommercialNoticeModel {
  tone: Tone;
  title: string;
  text: string;
  /** Solo el dueño recibe el botón (los demás no pueden contratar). */
  cta: string | null;
}

/**
 * El aviso global (encima de cada pantalla). Solo cuando el acceso está en riesgo AHORA: solo lectura
 * (todos los roles, para entender por qué no se puede registrar) o pago pendiente (dueño y gerente).
 * Durante el trial no hay aviso: no se molesta en el onboarding ni se inventa una cadencia de recordatorios.
 */
export function commercialNotice(status: BillingStatus | null, role: UserRole | undefined, timeZone?: string): CommercialNoticeModel | null {
  if (!status?.enabled) return null;
  const owner = role === 'OWNER';
  if (status.status === 'READ_ONLY') {
    return {
      tone: 'warning',
      title: 'Tu información está aquí.',
      text: owner
        ? 'Para seguir registrando movimientos, activa un plan.'
        : 'Puedes consultar todo. Para seguir registrando movimientos, la persona dueña del negocio necesita activar un plan.',
      cta: owner ? 'Activar plan' : null,
    };
  }
  if (status.status === 'PAST_DUE' && (role === 'OWNER' || role === 'MANAGER')) {
    // Lo normal dura hasta la gracia o hasta la cancelación programada, lo que pase antes (revisión 6b, R1).
    const until = formatBillingDate(status.graceEndsAt ? earliest(status.graceEndsAt, status.cancelEffectiveAt) : null, timeZone);
    return {
      tone: 'warning',
      title: 'Necesitamos actualizar tu forma de pago.',
      text: owner
        ? until ? `Puedes seguir trabajando con normalidad hasta el ${until}.` : 'Por ahora puedes seguir trabajando con normalidad.'
        : 'Avísale a la persona dueña del negocio para que la actualice.',
      cta: owner ? 'Actualizar pago' : null,
    };
  }
  return null;
}

const EVERY: Record<'day' | 'week' | 'month' | 'year', { one: string; many: string }> = {
  day: { one: 'al día', many: 'días' },
  week: { one: 'a la semana', many: 'semanas' },
  month: { one: 'al mes', many: 'meses' },
  year: { one: 'al año', many: 'años' },
};

/** ¿Hay un precio confirmado con el que se pueda contratar? (el servidor no lista planes sin él). */
export function hasConfirmedPrice(price: BillingPlan['price']): boolean {
  return Boolean(price && price.amountMinor !== null && price.interval !== null);
}

/** Precio tal como lo dio el proveedor de cobro (nunca inventado), con los decimales de SU moneda. */
export function priceText(price: BillingPlan['price']): string {
  if (!price || price.amountMinor === null || price.interval === null) return 'Precio no disponible por ahora.';
  const digits = price.minorUnits ?? 2;
  const amount = new Intl.NumberFormat('es-MX', {
    style: 'currency', currency: price.currency, currencyDisplay: 'code', minimumFractionDigits: digits, maximumFractionDigits: digits,
  }).format(price.amountMinor / 10 ** digits);
  const every = EVERY[price.interval];
  return price.intervalCount > 1 ? `${amount} cada ${price.intervalCount} ${every.many}` : `${amount} ${every.one}`;
}

export function limitText(label: string, limit: { unlimited: boolean; limit: number | null }): string {
  if (limit.unlimited) return `${label}: sin límite`;
  return limit.limit === null ? `${label}: —` : `${label}: hasta ${limit.limit}`;
}

/** El paso de regreso en el hash (`#billing/checkout-return`), si lo hay. */
export type BillingReturnStep = 'checkout-return' | 'checkout-canceled' | 'portal-return';
export function billingReturnStep(hash: string): BillingReturnStep | null {
  const step = hash.replace('#', '').split('/')[1];
  return step === 'checkout-return' || step === 'checkout-canceled' || step === 'portal-return' ? step : null;
}
